<template>
  <div class="flex flex-col">
    <!-- Search bar -->
    <div
      v-if="showSearch"
      class="border-b px-5 py-4"
      :class="dark ? 'border-white/10' : 'border-border'"
    >
      <div class="relative">
        <Icon
          name="tabler:search"
          :size="compact ? '16' : '18'"
          class="absolute left-3.5 top-1/2 -translate-y-1/2"
          :class="dark ? 'text-white/40' : 'text-muted-foreground'"
        />
        <input
          ref="searchInputRef"
          v-model="searchQuery"
          type="text"
          :placeholder="searchPlaceholder"
          class="flex w-full rounded-xl border py-2 text-base focus-visible:outline-none"
          :class="[
            compact ? 'h-10 pl-9 pr-3 text-sm' : 'h-12 pl-10 pr-4',
            dark
              ? 'border-white/20 bg-white/10 text-white placeholder:text-white/40'
              : 'border-border bg-muted/40 text-foreground placeholder:text-muted-foreground',
          ]"
          @keydown="handleKeyDown"
        />
      </div>
    </div>

    <!-- Language list -->
    <div
      ref="listRef"
      class="language-selector-list overflow-y-auto"
      :class="compact ? 'max-h-80' : 'flex-1'"
      role="listbox"
    >
      <div
        v-if="filteredLanguages.length === 0"
        class="px-5 py-6 text-center text-sm"
        :class="dark ? 'text-white/50' : 'text-muted-foreground'"
      >
        {{ noResultsText }}
      </div>
      <button
        v-for="(lang, index) in filteredLanguages"
        :key="lang.code"
        type="button"
        class="lang-item w-full flex items-center gap-4 px-5 py-3.5 text-left transition-colors"
        :class="[
          dark
            ? 'hover:bg-white/10 active:bg-white/10'
            : 'hover:bg-accent active:bg-accent',
          focusedIndex === index ? (dark ? 'bg-white/10' : 'bg-accent') : '',
          currentLocale === lang.code
            ? dark
              ? 'bg-white/15'
              : 'bg-primary/8'
            : '',
        ]"
        role="option"
        :aria-selected="currentLocale === lang.code"
        @click="$emit('select', lang.code)"
      >
        <span class="shrink-0 inline-flex rounded-full ring-2 ring-black/20">
          <Icon
            :name="`circle-flags:lang-${langToFlagCode(lang.code)}`"
            size="28"
            class="block"
          />
        </span>
        <div class="flex-1 min-w-0">
          <span
            class="block text-base font-semibold"
            :class="dark ? 'text-white' : 'text-foreground'"
          >
            {{ lang.nativeName || lang.name }}
          </span>
          <span
            v-if="lang.nativeName && lang.name && lang.nativeName !== lang.name"
            class="block text-sm"
            :class="dark ? 'text-white/50' : 'text-muted-foreground'"
          >
            {{ lang.name }}
          </span>
        </div>
        <Icon
          v-if="currentLocale === lang.code"
          name="tabler:check"
          size="18"
          :class="dark ? 'text-white/70' : 'text-primary'"
        />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted } from 'vue';
import { gsap } from 'gsap';
import type { LocaleInfo } from '../../../types/locale';
import { langToFlagCode } from '../../../utils/locale';

const props = withDefaults(
  defineProps<{
    /** Available locales to display */
    locales: LocaleInfo[];
    /** Currently selected locale code */
    currentLocale: string;
    /** Show the search input */
    showSearch?: boolean;
    /** Placeholder text for the search input */
    searchPlaceholder?: string;
    /** Text shown when search returns no results */
    noResultsText?: string;
    /** Dark mode (for footer / dark backgrounds) */
    dark?: boolean;
    /** Compact mode — smaller search input, constrained height */
    compact?: boolean;
    /** Auto-focus the search input when visible */
    autoFocusSearch?: boolean;
  }>(),
  {
    showSearch: false,
    searchPlaceholder: 'Search languages...',
    noResultsText: 'No languages found',
    dark: false,
    compact: false,
    autoFocusSearch: false,
  },
);

defineEmits<{
  select: [code: string];
}>();

const searchQuery = ref('');
const focusedIndex = ref(-1);
const searchInputRef = ref<HTMLInputElement | null>(null);
const listRef = ref<HTMLElement | null>(null);

/** Sort locales alphabetically by native name (or name fallback) for consistent ordering */
const sortedLocales = computed(() =>
  [...props.locales].sort((a, b) => {
    const nameA = (a.nativeName || a.name || a.code).toLowerCase();
    const nameB = (b.nativeName || b.name || b.code).toLowerCase();
    return nameA.localeCompare(nameB);
  }),
);

const filteredLanguages = computed(() => {
  if (!searchQuery.value) return sortedLocales.value;
  const q = searchQuery.value.toLowerCase();
  return sortedLocales.value.filter((lang) => {
    return (
      lang.code?.toLowerCase().includes(q) ||
      lang.name?.toLowerCase().includes(q) ||
      lang.nativeName?.toLowerCase().includes(q)
    );
  });
});

// Stagger items in when the list mounts (dropdown just opened)
onMounted(() => {
  nextTick(() => {
    const items = listRef.value?.querySelectorAll('.lang-item');
    if (items?.length) {
      gsap.fromTo(
        items,
        { opacity: 0, y: 6 },
        {
          opacity: 1,
          y: 0,
          duration: 0.22,
          stagger: 0.04,
          ease: 'power3.out',
          delay: 0.06,
          clearProps: 'transform,opacity',
        },
      );
    }
  });
});

const handleKeyDown = (event: KeyboardEvent) => {
  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault();
      focusedIndex.value =
        focusedIndex.value < filteredLanguages.value.length - 1
          ? focusedIndex.value + 1
          : 0;
      break;
    case 'ArrowUp':
      event.preventDefault();
      focusedIndex.value =
        focusedIndex.value > 0
          ? focusedIndex.value - 1
          : filteredLanguages.value.length - 1;
      break;
    case 'Enter':
      event.preventDefault();
      if (focusedIndex.value >= 0 && filteredLanguages.value[focusedIndex.value]) {
        // Emit from keyboard selection handled by parent
      }
      break;
  }
};

/** Focus the search input programmatically (called by parent) */
function focusSearch() {
  nextTick(() => searchInputRef.value?.focus());
}

/** Reset search query and focused index */
function reset() {
  searchQuery.value = '';
  focusedIndex.value = -1;
}

watch(
  () => props.autoFocusSearch,
  (val) => {
    if (val && props.showSearch) {
      focusSearch();
    }
  },
);

defineExpose({ focusSearch, reset });
</script>

<style scoped>
.language-selector-list {
  scrollbar-color: var(--scrollbar-thumb) transparent;
}

.language-selector-list:hover {
  scrollbar-color: auto;
}

/* WebKit browsers */
.language-selector-list::-webkit-scrollbar-thumb {
  background: var(--scrollbar-thumb);
  border-radius: 4px;
}

.language-selector-list::-webkit-scrollbar-track {
  background: transparent;
}

.language-selector-list:hover::-webkit-scrollbar-thumb {
  background: revert;
}

.language-selector-list:hover::-webkit-scrollbar-track {
  background: revert;
}
</style>
